/*
 */

import { Text } from '@/shared/ui';
import type { PartnershipResult } from '../context/PartnershipsWizardContext';
import { ResultCard } from './ResultCard';
import type { ResultCluster } from '../utils/results-grouping';

interface ResultsGroupProps {
  cluster: ResultCluster;
  countLabel: (count: number) => string;
}

/**
 * One geographic cluster of partner-search results: a sticky-feeling section
 * header (location label + a count pill) followed by the cluster's cards.
 *
 * The header turns the former bare "Imóveis em São Paulo / Pendente" rows into
 * a titled, counted section so the broker can scan the results by region. The
 * cards themselves are the canonical {@link ResultCard} — this component only
 * owns the grouping layout around them.
 *
 * @param props - The cluster to render and a localized count formatter.
 * @returns The cluster section.
 */
export function ResultsGroup({ cluster, countLabel }: ResultsGroupProps) {
  return (
    <section
      data-testid={`results-group-${cluster.key}`}
      className="flex flex-col gap-[var(--space-4)]"
    >
      <div className="flex items-center justify-between gap-[var(--space-3)] border-b border-[var(--border-subtle)] pb-[var(--space-3)]">
        <Text variant="body" size="sm" fw={600}>
          {cluster.label}
        </Text>
        <Text
          span
          size="xs"
          fw={500}
          className="shrink-0 rounded-(--radius-md) bg-[var(--surface-tertiary)] px-[var(--space-2)] py-[var(--space-1)] text-[var(--text-tertiary)]"
        >
          {countLabel(cluster.count)}
        </Text>
      </div>
      <div className="flex flex-col gap-[var(--space-4)]">
        {cluster.results.map((result: PartnershipResult, idx: number) => (
          <ResultCard key={result.url || `${cluster.key}-${idx}`} result={result} />
        ))}
      </div>
    </section>
  );
}
